import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously,
  linkWithCredential,
  linkWithPopup,
  getAdditionalUserInfo,
  EmailAuthProvider,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth'
import { auth } from '@/core/firebase/client'
import { trackLogin, trackSignUp } from '@/core/analytics'

// Thin wrapper over Firebase Auth for the player app. Mirrors clubmanager's
// auth-repository shape (sign-in / sign-up / google / sign-out / observe) but
// without any club/staff concepts — players are a flat user pool.
const googleProvider = new GoogleAuthProvider()

export const authRepository = {
  async signIn(email: string, password: string) {
    const cred = await signInWithEmailAndPassword(auth, email, password)
    trackLogin('email_password')
    return cred
  },
  /**
   * Sends the real Firebase password-reset email.
   *
   * Beta-readiness gap (2026-07-17, verified still open 2026-08-06): the
   * Recuperar-cuenta screen was a `setTimeout` that claimed "Revisa tu correo"
   * without calling any Firebase API. Email/password is a first-class sign-up
   * path here, so a player who forgot their password was permanently locked
   * out while being told help was on the way.
   */
  sendPasswordReset(email: string) {
    return sendPasswordResetEmail(auth, email)
  },
  async createUser(email: string, password: string) {
    // A guest (anonymous) session UPGRADES in place — linking keeps the uid,
    // so every match they joined by name stays theirs. Fresh visitors get a
    // normal signup.
    if (auth.currentUser?.isAnonymous) {
      const linked = await linkWithCredential(
        auth.currentUser,
        EmailAuthProvider.credential(email, password),
      )
      trackSignUp('email_password', true)
      return linked
    }
    const created = await createUserWithEmailAndPassword(auth, email, password)
    trackSignUp('email_password', false)
    return created
  },
  /**
   * Silent guest session — powers join-by-name from a shared link.
   * MUST await the persistence restore first: on a hard load
   * `auth.currentUser` is null until the initial snapshot resolves, and
   * calling signInAnonymously in that window REPLACES the player's real
   * session with a fresh guest (silent logout via a WhatsApp link — caught
   * live 2026-07-05).
   */
  async ensureSignedIn() {
    await auth.authStateReady()
    if (auth.currentUser) return auth.currentUser
    return signInAnonymously(auth).then((c) => c.user)
  },
  /**
   * Google sign-in — and, for a GUEST, an in-place upgrade.
   *
   * `createUser` has always linked the anonymous session so a link-guest keeps
   * their uid and their seat. This did not, and the two paths sit side by side
   * on the same screen: a player who joined from a WhatsApp link and then
   * tapped "Continuar con Google" got a BRAND NEW uid. Their seat stayed
   * behind on the anonymous account — still rendered in the match, still
   * holding a slot nobody could free — while their real account showed no
   * matches at all. The share door promises "Tu plaza ya es tuya igualmente";
   * for the Google half of the funnel that was false.
   *
   * Found 2026-08-08 while walking the real organizer flow (invite known
   * people, some on the platform and some not).
   *
   * The fallback matters: linking fails with `credential-already-in-use` when
   * that Google account already exists. There is no merge to do — the player
   * has a real account with real history, so we sign into it and let the
   * throwaway guest uid go.
   */
  async signInWithGoogle() {
    if (auth.currentUser?.isAnonymous) {
      try {
        const linked = await linkWithPopup(auth.currentUser, googleProvider)
        trackSignUp('google', true)
        return linked
      } catch (e) {
        const code = (e as { code?: string }).code
        if (code !== 'auth/credential-already-in-use' && code !== 'auth/email-already-in-use') throw e
      }
    }
    const cred = await signInWithPopup(auth, googleProvider)
    // Firebase is the only thing that knows whether this Google account is new;
    // without it a first-ever Google signup would report as a `login` and the
    // signup count would be permanently short.
    const info = getAdditionalUserInfo(cred)
    if (info?.isNewUser) trackSignUp('google', false)
    else trackLogin('google')
    return cred
  },
  signOut() {
    return fbSignOut(auth)
  },
  observe(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback)
  },
}
